// src/components/NewsJsonLd.jsx
import React from 'react';

/**
 * Verbesserte NewsArticle JSON-LD Komponente
 *
 * Props (neu/erweitert):
 * - url: string
 * - headline: string
 * - description: string
 * - articleSection?: string
 * - genre?: string | string[]
 * - keywords?: string[] | string | object
 * - inLanguage?: string
 * - datePublished: string|Date
 * - dateModified?: string|Date
 * - images: {url:string,width?:number,height?:number}[]      // mind. 1 großes Bild empfohlen (>=1200 px)
 * - thumbnailUrl?: string                                     // optional kleines Vorschau-Bild
 * - authors?: {name:string,url?:string,'@type'?:'Person'|'Organization'}[]
 * - publisherName: string
 * - publisherLogoUrl: string
 * - publisherLogoWidth?: number
 * - publisherLogoHeight?: number
 * - isAccessibleForFree?: boolean
 * - mainEntityOfPage?: string | { '@type': 'WebPage', '@id': string }
 * - wordCount?: number
 * - articleBody?: string
 * - speakable?: boolean | { cssSelector?: string[]; xpath?: string[] }  // optional
 */
export default function NewsJsonLd({
    url,
    headline,
    description,
    articleSection,
    genre,
    keywords = [],
    inLanguage = 'de',
    datePublished,
    dateModified,
    images = [],
    thumbnailUrl,
    authors = [],
    publisherName,
    publisherLogoUrl,
    publisherLogoWidth,
    publisherLogoHeight,
    isAccessibleForFree = true,
    mainEntityOfPage,
    wordCount,
    articleBody,
    speakable,
}) {
    const toIsoSafe = (d) => {
        if (!d) return undefined;
        try {
            const iso = (d instanceof Date ? d : new Date(d)).toISOString();
            return isNaN(Date.parse(iso)) ? undefined : iso;
        } catch { return undefined; }
    };

    const toArray = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val.filter(Boolean).map(String);
        if (typeof val === 'string') {
            try {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
            } catch { /* noop */ }
            return val.split(/[;,#]/).map(s => s.trim()).filter(Boolean);
        }
        if (typeof val === 'object') return Object.values(val).map(String).filter(Boolean);
        return [];
    };

    const genreArr = toArray(genre);
    const keywordArr = toArray(keywords);

    // Autoren robust aufbauen; wenn leer, Publisher als Organization nutzen
    const authorNodes = (authors && authors.length ? authors : [{ name: publisherName, '@type': 'Organization' }])
        .map(a => ({
            '@type': a['@type'] || 'Person',
            name: a.name,
            ...(a.url ? { url: a.url } : {}),
        }));

    const imageNodes = (images || []).map(img => ({
        '@type': 'ImageObject',
        url: img.url,
        ...(img.width ? { width: img.width } : {}),
        ...(img.height ? { height: img.height } : {}),
    }));

    const data = {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        url,

        // mainEntityOfPage: akzeptiert string oder Objekt
        mainEntityOfPage: typeof mainEntityOfPage === 'string'
            ? { '@type': 'WebPage', '@id': mainEntityOfPage }
            : (mainEntityOfPage || { '@type': 'WebPage', '@id': url }),

        headline,
        description,
        inLanguage,

        ...(articleSection ? { articleSection } : {}),
        ...(genreArr.length ? { genre: genreArr } : {}),
        ...(keywordArr.length ? { keywords: keywordArr } : {}),

        datePublished: toIsoSafe(datePublished),
        dateModified: toIsoSafe(dateModified || datePublished),

        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        ...(imageNodes.length ? { image: imageNodes } : {}),

        author: authorNodes,

        publisher: {
            '@type': 'Organization',
            name: publisherName,
            logo: {
                '@type': 'ImageObject',
                url: publisherLogoUrl,
                ...(publisherLogoWidth ? { width: publisherLogoWidth } : {}),
                ...(publisherLogoHeight ? { height: publisherLogoHeight } : {}),
            },
        },

        ...(typeof wordCount === 'number' ? { wordCount } : {}),
        ...(articleBody ? { articleBody } : {}),

        isAccessibleForFree,

        ...(isAccessibleForFree ? {} : {
            hasPart: {
                '@type': 'WebPageElement',
                isAccessibleForFree: 'False',
                cssSelector: '.paywalled',
            },
        }),

        // Optional: speakable (für Voice/Assistenten) – vorsichtig einsetzen
        ...(speakable
            ? {
                speakable: typeof speakable === 'object'
                    ? {
                        '@type': 'SpeakableSpecification',
                        ...(speakable.cssSelector ? { cssSelector: speakable.cssSelector } : {}),
                        ...(speakable.xpath ? { xpath: speakable.xpath } : {}),
                    }
                    : { '@type': 'SpeakableSpecification', cssSelector: ['h1', '.news-body p:first-of-type'] },
            }
            : {}),
    };

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
    );
}
